import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Loader2,
  RotateCcw,
  Save,
  AlertTriangle,
  FileText,
  RefreshCw,
  Eye,
  CheckCircle2,
  Sparkles,
  Wand2,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function normalizeMarc(value) {
  // Accept comma/space/semicolon separators, fix "0900" → "09:00"
  return String(value || "")
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => {
      if (HHMM.test(t)) return t;
      const digits = t.replace(/\D/g, "");
      if (digits.length === 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
      if (digits.length === 3) return `0${digits[0]}:${digits.slice(1)}`;
      return t;
    });
}

function dowOf(dateStr) {
  if (!dateStr || !/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return "";
  const [d, m, y] = dateStr.split("/").map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"][wd];
}

function parseDateKey(s) {
  const [d, m, y] = s.split("/").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getTime();
}

export default function AdminAfdEditor({
  open,
  onClose,
  jobId,
  bucket,
  token,
  onSavedAny,
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [savingDate, setSavingDate] = useState("");
  const [drafts, setDrafts] = useState({}); // {date: {marcacoes:"text", abono:"text"}}
  const [addDate, setAddDate] = useState("");
  const [autoFixing, setAutoFixing] = useState(false);
  const pdfBlobRef = useRef("");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const empKey = bucket?.emp_key || "";

  const loadDetail = useCallback(async () => {
    if (!jobId || !bucket) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/afd/job/${jobId}/bucket/${encodeURIComponent(
          bucket.bucket_id
        )}`,
        { headers }
      );
      setDetail(data);
      setDrafts({});
    } catch (err) {
      toast.error(
        `Erro ao carregar marcações: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setLoading(false);
    }
  }, [jobId, bucket, headers]);

  const loadPdf = useCallback(async () => {
    if (!jobId || !bucket) return;
    setPdfLoading(true);
    try {
      const resp = await axios.get(
        `${API}/admin/afd/job/${jobId}/bucket/${encodeURIComponent(
          bucket.bucket_id
        )}/pdf`,
        { headers, responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      if (pdfBlobRef.current) URL.revokeObjectURL(pdfBlobRef.current);
      const url = URL.createObjectURL(blob);
      pdfBlobRef.current = url;
      setPdfUrl(url);
    } catch (err) {
      toast.error(`Não foi possível gerar o preview: ${err.message}`);
    } finally {
      setPdfLoading(false);
    }
  }, [jobId, bucket, headers]);

  useEffect(() => {
    if (open && bucket) {
      loadDetail();
      loadPdf();
    }
    return () => {
      if (!open && pdfBlobRef.current) {
        URL.revokeObjectURL(pdfBlobRef.current);
        pdfBlobRef.current = "";
        setPdfUrl("");
      }
    };
  }, [open, bucket, loadDetail, loadPdf]);

  // Build a unified per-day map from raw_by_day ∪ corrections
  const dayRows = useMemo(() => {
    if (!detail) return [];
    const merged = {};
    const raw = detail.raw_by_day || {};
    const corr = detail.corrections || {};
    for (const [d, list] of Object.entries(raw)) {
      merged[d] = {
        date: d,
        raw_marcacoes: list || [],
        correction: corr[d] || null,
      };
    }
    for (const [d, c] of Object.entries(corr)) {
      if (!merged[d]) {
        merged[d] = { date: d, raw_marcacoes: [], correction: c };
      }
    }
    return Object.values(merged).sort(
      (a, b) => parseDateKey(a.date) - parseDateKey(b.date)
    );
  }, [detail]);

  const getDraft = (date, row) => {
    if (drafts[date]) return drafts[date];
    const comentario = row.correction?.comentario || "";
    if (row.correction?.abono) {
      return {
        marcacoes: "",
        abono: row.correction.abono.observacao || row.correction.abono.tipo || "",
        comentario,
        mode: "abono",
      };
    }
    if (row.correction?.marcacoes) {
      return {
        marcacoes: row.correction.marcacoes.join(", "),
        abono: "",
        comentario,
        mode: "marcacoes",
      };
    }
    return {
      marcacoes: (row.raw_marcacoes || []).join(", "),
      abono: "",
      comentario,
      mode: "marcacoes",
    };
  };

  const updateDraft = (date, patch) => {
    setDrafts((d) => ({
      ...d,
      [date]: { ...getDraft(date, dayRows.find((r) => r.date === date) || {}), ...patch },
    }));
  };

  const saveDate = async (date) => {
    const row = dayRows.find((r) => r.date === date);
    const d = getDraft(date, row || {});
    setSavingDate(date);
    try {
      const comentario = (d.comentario || "").trim();
      let payload;
      if (d.mode === "abono") {
        const obs = (d.abono || "").trim();
        if (!obs) {
          toast.error("Descreva o abono antes de salvar.");
          setSavingDate("");
          return;
        }
        payload = {
          date,
          marcacoes: null,
          abono: { tipo: "Abono", observacao: obs },
          comentario: comentario || null,
        };
      } else {
        const marcs = normalizeMarc(d.marcacoes);
        const bad = marcs.filter((m) => !HHMM.test(m));
        if (bad.length) {
          toast.error(`Horário inválido: ${bad.join(", ")} (use HH:MM)`);
          setSavingDate("");
          return;
        }
        payload = {
          date,
          marcacoes: marcs,
          abono: null,
          comentario: comentario || null,
        };
      }
      await axios.put(
        `${API}/admin/afd/corrections/${encodeURIComponent(empKey)}`,
        payload,
        { headers }
      );
      toast.success(`Correção salva para ${date}`);
      setDrafts((s) => {
        const c = { ...s };
        delete c[date];
        return c;
      });
      await loadDetail();
      await loadPdf();
      onSavedAny?.();
    } catch (err) {
      toast.error(
        `Erro ao salvar: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setSavingDate("");
    }
  };

  const revertDate = async (date) => {
    setSavingDate(date);
    try {
      await axios.put(
        `${API}/admin/afd/corrections/${encodeURIComponent(empKey)}`,
        { date, marcacoes: null, abono: null },
        { headers }
      );
      toast.success(`Restaurado AFD original em ${date}`);
      setDrafts((s) => {
        const c = { ...s };
        delete c[date];
        return c;
      });
      await loadDetail();
      await loadPdf();
      onSavedAny?.();
    } catch (err) {
      toast.error(
        `Erro ao reverter: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setSavingDate("");
    }
  };

  const autoFix = async () => {
    if (!jobId || !bucket) return;
    if (
      !window.confirm(
        "Aplicar auto-correção neste colaborador?\n\n" +
          "• Dedup ≤3 min\n" +
          "• Insere saída em 12h±15min onde o intervalo > 18h\n" +
          "• Pares <4h viram abono 'Inconsistência — revisar'\n" +
          "• Dias já corrigidos por você ficam intactos"
      )
    )
      return;
    setAutoFixing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/afd/job/${jobId}/auto-fix/${encodeURIComponent(
          bucket.bucket_id
        )}`,
        null,
        { headers }
      );
      if (data.skipped_non_12x36) {
        toast.warning(
          "Este colaborador não tem padrão 12x36 (provavelmente 4 batidas/dia). Auto-correção não aplicada."
        );
      } else {
        toast.success(
          `${data.days_touched} dias corrigidos · ` +
            `${data.missing_punches_inserted} saídas inseridas · ` +
            `${data.dedup_removed} duplicadas removidas · ` +
            `${data.abono_days} abonos`,
          { duration: 6500 }
        );
      }
      await loadDetail();
      await loadPdf();
      onSavedAny?.();
    } catch (err) {
      toast.error(
        `Erro na auto-correção: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setAutoFixing(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Remover TODAS as correções deste funcionário?")) return;
    try {
      await axios.delete(
        `${API}/admin/afd/corrections/${encodeURIComponent(empKey)}`,
        { headers }
      );
      toast.success("Correções limpas.");
      await loadDetail();
      await loadPdf();
      onSavedAny?.();
    } catch (err) {
      toast.error(`Erro: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const addNewDay = () => {
    const v = addDate.trim();
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
      toast.error("Data inválida — use DD/MM/AAAA");
      return;
    }
    if (dayRows.some((r) => r.date === v)) {
      toast.warning("Esta data já está na lista. Edite a linha existente.");
      setAddDate("");
      return;
    }
    setDetail((d) => ({
      ...d,
      raw_by_day: { ...(d?.raw_by_day || {}), [v]: [] },
    }));
    updateDraft(v, { marcacoes: "", abono: "", mode: "marcacoes" });
    setAddDate("");
    // Scroll to bottom (next tick)
    setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="afd-day-row-${v}"]`
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-[1200px] w-[96vw] max-h-[92vh] p-0 overflow-hidden flex flex-col"
        data-testid="afd-editor-dialog"
      >
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="text-base font-heading flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Auditoria de Ponto — {bucket?.nome_db || bucket?.nome_afd || "—"}
            {bucket?.tem_correcoes ? (
              <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-200">
                <Sparkles className="w-3 h-3 mr-1" /> com correções
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Editor de auditoria de marcações de ponto do colaborador, com tabela
            editável por dia e preview do espelho em PDF.
          </DialogDescription>
          <div className="text-xs text-slate-500 flex gap-3 flex-wrap mt-1">
            {bucket?.matricula ? (
              <span>
                <b className="text-slate-700">Matrícula:</b> {bucket.matricula}
              </span>
            ) : null}
            {bucket?.cpf ? (
              <span>
                <b className="text-slate-700">CPF:</b> {bucket.cpf}
              </span>
            ) : null}
            {bucket?.pis ? (
              <span>
                <b className="text-slate-700">PIS:</b> {bucket.pis}
              </span>
            ) : null}
            <span>
              <b className="text-slate-700">Pares:</b> {bucket?.pares_total ?? 0}{" "}
              · incompletos {bucket?.pares_incompletos ?? 0} · &gt;24h{" "}
              {bucket?.turnos_longos ?? 0}
            </span>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 overflow-hidden">
          {/* Editor */}
          <div className="border-r border-slate-200 flex flex-col overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap">
              <Input
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
                placeholder="DD/MM/AAAA"
                className="h-8 w-36 text-xs"
                data-testid="afd-add-date-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={addNewDay}
                className="h-8 text-xs"
                data-testid="afd-add-date-btn"
              >
                + dia
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="ghost"
                onClick={autoFix}
                disabled={autoFixing}
                className="h-8 text-xs text-violet-700 hover:text-violet-800 hover:bg-violet-50"
                data-testid="afd-autofix-bucket-btn"
                title="Aplica heurística 12x36: dedup, inserção de saída esquecida, abono em pares <4h"
              >
                {autoFixing ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Wand2 className="w-3.5 h-3.5 mr-1" />
                )}
                Auto-corrigir
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearAll}
                className="h-8 text-xs text-red-600 hover:text-red-700"
                disabled={!detail?.corrections || !Object.keys(detail.corrections || {}).length}
                data-testid="afd-clear-all-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                Reverter tudo
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Carregando marcações...
                </div>
              ) : dayRows.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">
                  Nenhuma marcação registrada para este colaborador.
                </div>
              ) : (
                <table className="w-full text-xs" data-testid="afd-day-table">
                  <thead className="bg-slate-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left py-2 px-2 w-24">Data</th>
                      <th className="text-left py-2 px-2 w-12">Dia</th>
                      <th className="text-left py-2 px-2">Marcações / Abono</th>
                      <th className="text-right py-2 px-2 w-32">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map((row) => {
                      const d = getDraft(row.date, row);
                      const isAbono = d.mode === "abono";
                      const hasCorrection = !!row.correction;
                      const isDirty = !!drafts[row.date];
                      const isOdd =
                        d.mode === "marcacoes" &&
                        normalizeMarc(d.marcacoes).length % 2 === 1;
                      return (
                        <tr
                          key={row.date}
                          className={`border-t border-slate-100 align-top ${
                            hasCorrection ? "bg-amber-50/40" : ""
                          }`}
                          data-testid={`afd-day-row-${row.date}`}
                        >
                          <td className="py-2 px-2 font-mono text-slate-700">
                            {row.date}
                          </td>
                          <td className="py-2 px-2 text-slate-500">
                            {dowOf(row.date)}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2 text-[10px]">
                                <button
                                  className={`px-2 py-0.5 rounded border ${
                                    !isAbono
                                      ? "bg-blue-600 text-white border-blue-600"
                                      : "bg-white text-slate-600 border-slate-300"
                                  }`}
                                  onClick={() =>
                                    updateDraft(row.date, { mode: "marcacoes" })
                                  }
                                  data-testid={`afd-mode-marc-${row.date}`}
                                >
                                  marcações
                                </button>
                                <button
                                  className={`px-2 py-0.5 rounded border ${
                                    isAbono
                                      ? "bg-amber-600 text-white border-amber-600"
                                      : "bg-white text-slate-600 border-slate-300"
                                  }`}
                                  onClick={() =>
                                    updateDraft(row.date, { mode: "abono" })
                                  }
                                  data-testid={`afd-mode-abono-${row.date}`}
                                >
                                  abono
                                </button>
                                {isOdd ? (
                                  <span className="text-red-600 inline-flex items-center text-[10px]">
                                    <AlertTriangle className="w-3 h-3 mr-0.5" />
                                    ímpar
                                  </span>
                                ) : null}
                                {hasCorrection ? (
                                  <span className="text-amber-700 inline-flex items-center text-[10px]">
                                    editado
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  className={`ml-auto px-2 py-0.5 rounded border inline-flex items-center gap-1 ${
                                    d.comentario
                                      ? "bg-violet-100 text-violet-800 border-violet-300"
                                      : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                                  }`}
                                  onClick={() =>
                                    updateDraft(row.date, {
                                      _showComment: !d._showComment,
                                    })
                                  }
                                  data-testid={`afd-toggle-comment-${row.date}`}
                                  title="Adicionar comentário visível no PDF"
                                >
                                  <MessageSquare className="w-3 h-3" />
                                  {d.comentario ? "com obs." : "obs."}
                                </button>
                              </div>
                              {isAbono ? (
                                <Input
                                  value={d.abono}
                                  onChange={(e) =>
                                    updateDraft(row.date, {
                                      abono: e.target.value,
                                    })
                                  }
                                  placeholder="Descreva o abono (ex: Atestado médico de 8h)"
                                  className="h-8 text-xs"
                                  data-testid={`afd-abono-input-${row.date}`}
                                />
                              ) : (
                                <Input
                                  value={d.marcacoes}
                                  onChange={(e) =>
                                    updateDraft(row.date, {
                                      marcacoes: e.target.value,
                                    })
                                  }
                                  placeholder="07:00, 11:00, 13:00, 17:00"
                                  className={`h-8 text-xs font-mono ${
                                    isOdd ? "border-red-300" : ""
                                  }`}
                                  data-testid={`afd-marc-input-${row.date}`}
                                />
                              )}
                              {!isAbono &&
                              row.raw_marcacoes?.length &&
                              hasCorrection ? (
                                <div className="text-[10px] text-slate-400">
                                  AFD original: {row.raw_marcacoes.join(", ")}
                                </div>
                              ) : null}
                              {!isAbono &&
                              normalizeMarc(d.marcacoes).length === 4 ? (
                                <div className="text-[10px] text-blue-700 font-mono">
                                  {(() => {
                                    const m = normalizeMarc(d.marcacoes).sort();
                                    return `ENT ${m[0]} · ALM ${m[1]} · RET ${m[2]} · SAI ${m[3]}`;
                                  })()}
                                </div>
                              ) : null}
                              {!isAbono &&
                              normalizeMarc(d.marcacoes).length === 2 ? (
                                <div className="text-[10px] text-slate-500 font-mono">
                                  {(() => {
                                    const m = normalizeMarc(d.marcacoes).sort();
                                    return `ENT ${m[0]} · SAI ${m[1]}`;
                                  })()}
                                </div>
                              ) : null}
                              {(d._showComment || d.comentario) ? (
                                <textarea
                                  value={d.comentario || ""}
                                  onChange={(e) =>
                                    updateDraft(row.date, {
                                      comentario: e.target.value,
                                    })
                                  }
                                  rows={2}
                                  maxLength={280}
                                  placeholder="Observação que aparecerá no PDF (ex: Justificativa de troca de turno)"
                                  className="w-full text-[11px] px-2 py-1.5 border border-violet-300 bg-violet-50/50 rounded focus:outline-none focus:ring-1 focus:ring-violet-400"
                                  data-testid={`afd-comment-input-${row.date}`}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => saveDate(row.date)}
                              disabled={savingDate === row.date || !isDirty}
                              className="h-7 px-2 text-xs"
                              data-testid={`afd-save-${row.date}`}
                            >
                              {savingDate === row.date ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Save className="w-3 h-3 mr-1" />
                              )}
                              salvar
                            </Button>
                            {hasCorrection ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => revertDate(row.date)}
                                disabled={savingDate === row.date}
                                className="h-7 px-2 text-xs text-red-600 hover:text-red-700"
                                data-testid={`afd-revert-${row.date}`}
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                rev.
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* PDF preview */}
          <div className="bg-slate-100 flex flex-col">
            <div className="px-3 py-2 border-b border-slate-200 bg-white flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-medium text-slate-700">
                Preview do Espelho de Ponto
              </span>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                onClick={loadPdf}
                disabled={pdfLoading}
                className="h-7 text-xs"
                data-testid="afd-refresh-pdf"
              >
                {pdfLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                atualizar
              </Button>
            </div>
            <div className="flex-1 relative">
              {pdfLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                </div>
              ) : null}
              {pdfUrl ? (
                <iframe
                  title="Espelho PDF"
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  data-testid="afd-pdf-iframe"
                />
              ) : (
                <div className="p-8 text-center text-slate-400 text-sm">
                  Gerando preview...
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            Correções são salvas por dia. Use "Reconstruir ZIP" para aplicar a todos.
          </div>
          <Button onClick={onClose} variant="outline" size="sm" data-testid="afd-editor-close">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
